import React, { useEffect, useMemo, useState } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { EmptyState } from '../common/EmptyState';
import { Tag } from '../common/Tag';
import { fmtDate, fmtDateTime, nowISO } from '../../utils/helpers';
import { STD_TEMPLATE } from '../../utils/constants';
import ResponseCapture from './ResponseCapture';
import ComparisonView from './ComparisonView';
import { feasibilityAssessmentServices } from '../../APIServices/feasibilityAssessmentServices';
import { studyManagementServices } from '../../APIServices/studyManagementServices';

// Helper function to calculate scores
const calculateScores = (template, responses) => {
  const sections = template.sections.map((section) => {
    let weightedSum = 0;
    let maxWeightedSum = 0;
    let missing = 0;

    section.questions.forEach((q) => {
      const r = responses[q.id];
      let score = null;
      if (r == null || r === '') {
        missing++;
        maxWeightedSum += q.weight * 10;
      } else {
        if (q.type === 'yesno') score = r === 'yes' ? 10 : r === 'partial' ? 5 : 0;
        if (q.type === 'scale') score = Number(r) || 0;
        if (score != null) {
          weightedSum += score * q.weight;
          maxWeightedSum += q.weight * 10;
        }
      }
    });

    const pct = maxWeightedSum > 0 ? Math.round((weightedSum / maxWeightedSum) * 100) : 0;
    return { id: section.id, name: section.name, weight: section.weight, score: pct, missing };
  });

  let totalWeighted = 0;
  let totalWeight = 0;
  sections.forEach((s) => {
    totalWeighted += s.score * s.weight;
    totalWeight += s.weight;
  });

  const overall = totalWeight > 0 ? Math.round(totalWeighted / totalWeight) : 0;

  let band, bandLabel, bandVariant;
  if (overall >= 85) {
    band = 'strong';
    bandLabel = 'Strong fit / Low risk';
    bandVariant = 'ok';
  } else if (overall >= 70) {
    band = 'acceptable';
    bandLabel = 'Acceptable / Medium risk';
    bandVariant = 'info';
  } else if (overall >= 50) {
    band = 'conditional';
    bandLabel = 'Conditional / High risk';
    bandVariant = 'warn';
  } else {
    band = 'reject';
    bandLabel = 'Not recommended / Critical risk';
    bandVariant = 'danger';
  }

  return { sections, overall, band, bandLabel, bandVariant };
};

function FeasibilityDetailPage({ feasibilityId, onBack }) {
  const { state, currentUser } = useData();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [feasibility, setFeasibility] = useState(null);
  const [study, setStudy] = useState(null);
  const [step, setStep] = useState('respond');
  const [activeCRO, setActiveCRO] = useState('');
  const [rationale, setRationale] = useState('');
  const [chosenCRO, setChosenCRO] = useState('');

  const isLocked = Boolean(feasibility?.locked) || feasibility?.status === 'approved';

  const getFeasibilityById = async () => {
    setLoading(true);
    try {
      const data = await feasibilityAssessmentServices.getFeasibilityAssessmentData(
        'all',
        '',
        1,
        1,
        { assessmentId: feasibilityId }
      );

      const row = data?.data?.[0] || null;
      setFeasibility(row);

      if (row?.status === 'approved') {
        setStep('recommend');
      } else if (row?.status === 'in_review') {
        setStep('compare');
      } else {
        setStep('respond');
      }

      setActiveCRO((prev) => prev || row?.cros?.[0] || '');
      setChosenCRO((prev) => prev || row?.recommendedCRO || '');
      setRationale((prev) => prev || row?.rationale || '');

      if (row?.studyId) {
        const studiesData = await studyManagementServices.getStudyManagement('all', '', 1, 1000);
        const studies = studiesData?.data || [];
        const linkedStudy = studies.find((item) => String(item.studyId) === String(row.studyId));
        setStudy(linkedStudy || null);
      } else {
        setStudy(null);
      }
    } catch (error) {
      setFeasibility(null);
      setStudy(null);
      toast('Unable to load feasibility details.', 'warn');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFeasibilityById();
  }, [feasibilityId]);

  const scoresByCRO = useMemo(() => {
    const m = {};
    if (feasibility) {
      (feasibility.cros || []).forEach((cro) => {
        m[cro] = calculateScores(STD_TEMPLATE, feasibility.responses?.[cro] || {});
      });
    }
    return m;
  }, [feasibility]);

  const updateFeasibility = async (payload, successMessage) => {
    if (!feasibility?._id) {
      return;
    }

    try {
      await feasibilityAssessmentServices.updateFeasibilityAssessmentData({
        id: feasibility._id,
        ...payload,
      });

      setFeasibility((prev) => ({
        ...(prev || {}),
        ...payload,
      }));

      if (successMessage) {
        toast(successMessage, 'ok');
      }
    } catch (error) {
      toast('Unable to update feasibility assessment.', 'warn');
    }
  };

  const updateResponses = async (nextFeasibility) => {
    await updateFeasibility({ responses: nextFeasibility.responses });
  };

  if (!feasibility) {
    if (loading) {
      return <EmptyState icon="ti-loader" title="Loading feasibility details..." />;
    }
    return <EmptyState icon="ti-checklist" title="Not found" />;
  }

  const handleSubmitForReview = () => {
    updateFeasibility({ status: 'in_review' }, 'Submitted for review');
    setStep('compare');
  };

  const handleApprove = () => {
    if (!chosenCRO || !rationale) {
      toast('Select CRO and provide rationale', 'warn');
      return;
    }
    const lockedVersion = (feasibility.version || 1) + 1;
    updateFeasibility({
      status: 'approved',
      version: lockedVersion,
      approvedAt: nowISO(),
      approvedBy: currentUser?.id,
      recommendedCRO: chosenCRO,
      rationale,
      locked: true
    }, `${feasibility.code} approved · locked at v${lockedVersion}`);
  };

  return (
    <div>
      <div className="flex gap-2 center mb-3">
        <button className="btn btn-sm btn-ghost" onClick={onBack}>
          <i className="ti ti-arrow-left" /> Back
        </button>
      </div>

      <div className="page-header">
        <div>
          <div className="page-eyebrow">{feasibility.code} · {study?.studyCode || '—'}</div>
          <h1 className="page-title" style={{ fontSize: 26 }}>{feasibility.title}</h1>
          <div className="flex gap-2 mt-2 center">
            {feasibility.status === 'approved' ? (
              <Tag variant="ok" dot>Approved · v{feasibility.version} · locked</Tag>
            ) : feasibility.status === 'in_review' ? (
              <Tag variant="warn" dot>In review</Tag>
            ) : (
              <Tag dot>In progress</Tag>
            )}
            <span className="xs muted">Created {fmtDate(feasibility.createdAt)} · Due {fmtDate(feasibility.dueAt)}</span>
          </div>
        </div>
        {!isLocked && (
          <div className="flex gap-2">
            {step === 'respond' && (
              <button className="btn btn-primary" onClick={handleSubmitForReview}>
                <i className="ti ti-send" /> Submit for review
              </button>
            )}
          </div>
        )}
      </div>

      <div className="steps">
        <div className={`step ${step === 'respond' ? 'current' : 'done'}`} onClick={() => !isLocked && setStep('respond')}>
          <span className="step-num">1</span> Capture responses
        </div>
        <div className={`step ${step === 'compare' ? 'current' : step === 'recommend' ? 'done' : ''}`} onClick={() => setStep('compare')}>
          <span className="step-num">2</span> Compare & analyze
        </div>
        <div className={`step ${step === 'recommend' ? 'current' : ''} ${feasibility.status === 'approved' ? 'done' : ''}`} onClick={() => setStep('recommend')}>
          <span className="step-num">3</span> Approve & lock
        </div>
      </div>

      {step === 'respond' && (
        <>
          <div className="flex gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
            {(feasibility.cros || []).map((cro) => {
              const s = scoresByCRO[cro];
              return (
                <button
                  key={cro}
                  className={`btn btn-sm ${activeCRO === cro ? 'btn-primary' : ''}`}
                  disabled={isLocked}
                  onClick={() => setActiveCRO(cro)}
                >
                  {cro}
                  <span className="tag tiny" style={{ marginLeft: 6, background: 'rgba(255,255,255,0.15)', color: 'inherit', border: 'none' }}>
                    {s?.overall || 0}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="card card-pad">
            <div className="card-title">Responses — {activeCRO}</div>
            {activeCRO && (
              <ResponseCapture
                feasibility={feasibility}
                cro={activeCRO}
                readOnly={isLocked}
                onUpdate={updateResponses}
              />
            )}
          </div>
        </>
      )}

      {step === 'compare' && <ComparisonView feasibility={feasibility} />}

      {step === 'recommend' && (
        <div className="row">
          <div className="col">
            <ComparisonView feasibility={feasibility} />
          </div>
          <div style={{ width: 380 }}>
            <div className="card card-pad">
              <div className="card-title">Recommendation & lock</div>

              {feasibility.status === 'approved' ? (
                <>
                  <Tag variant="ok" dot>Locked v{feasibility.version}</Tag>
                  <div className="sm mt-3">
                    <div className="muted xs mb-1">Recommended CRO</div>
                    <div className="bold">{feasibility.recommendedCRO}</div>
                  </div>
                  <div className="sm mt-3">
                    <div className="muted xs mb-1">Rationale</div>
                    <div>{feasibility.rationale}</div>
                  </div>
                  <div className="sm mt-3">
                    <div className="muted xs mb-1">Approved by</div>
                    <div>{state.users.find((u) => u.id === feasibility.approvedBy)?.name} · {fmtDateTime(feasibility.approvedAt)}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="field">
                    <label className="field-label">Recommended CRO</label>
                    <select value={chosenCRO} onChange={(e) => setChosenCRO(e.target.value)}>
                      <option value="">Select...</option>
                      {Object.entries(scoresByCRO)
                        .sort((a, b) => b[1].overall - a[1].overall)
                        .map(([cro, s]) => (
                          <option key={cro} value={cro}>{cro} · {s.overall}/100 · {s.bandLabel}</option>
                        ))}
                    </select>
                  </div>

                  <div className="field">
                    <label className="field-label">Rationale & decision notes</label>
                    <textarea
                      rows={6}
                      value={rationale}
                      onChange={(e) => setRationale(e.target.value)}
                      placeholder="State the basis for recommendation: weighted score, risk profile, alignment with study requirements, audit considerations, etc."
                    />
                  </div>

                  <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleApprove}>
                    <i className="ti ti-lock" /> Approve & lock version
                  </button>

                  <div className="xs muted mt-3" style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
                    Once approved, this version is immutable. Subsequent edits create a new version with audit trail.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeasibilityDetailPage;